import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { translations, supportedLanguages, languageFlags, languageNames } from '../utils/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// v50.10.9 — first launch: pick the closest of the 13 app languages from the phone/browser language.
const detectDeviceLanguage = () => {
  try {
    const cands = [...(navigator.languages || []), navigator.language].filter(Boolean).map((l) => l.toLowerCase());
    for (const c of cands) {
      const base = c.split('-')[0];
      if (base === 'ht' || c.startsWith('fr-ht')) return 'ht';
      if (supportedLanguages.includes(base)) return base;
    }
  } catch (e) { /* ignore */ }
  return null;
};

export const LanguageProvider = ({ children }) => {
  const needsGeo = React.useRef(false);
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('destine_language');
    if (saved && supportedLanguages.includes(saved)) return saved;
    const detected = detectDeviceLanguage();
    needsGeo.current = !detected;
    return detected || 'fr';
  });

  // No saved choice and device language not supported → fallback on IP geolocation (country → language).
  useEffect(() => {
    if (!needsGeo.current) return;
    axios.get('/api/geo/suggest-language')
      .then((r) => { const l = r.data?.language; if (l && supportedLanguages.includes(l)) setLanguageState(l); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem('destine_language', language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const t = (path, fallback = '') => {
    if (!path || typeof path !== 'string') return fallback || '';
    const keys = path.split('.');
    let result = translations[language];
    
    for (const key of keys) {
      if (result && typeof result === 'object' && key in result) {
        result = result[key];
      } else {
        result = translations['en'];
        for (const k of keys) {
          if (result && typeof result === 'object' && k in result) {
            result = result[k];
          } else {
            return fallback || path;
          }
        }
        break;
      }
    }
    
    return result || fallback || path;
  };

  const changeLanguage = (lang) => {
    if (supportedLanguages.includes(lang)) {
      setLanguageState(lang);
      // Sync with backend
      const token = localStorage.getItem('auth_token');
      if (token) {
        axios.put('/api/users/language', { language: lang }).catch(() => {});
      }
    }
  };

  const getLanguageName = (code) => {
    return translations[language]?.languages?.[code] || languageNames[code]?.native || code;
  };

  const getFlag = (code) => {
    return languageFlags[code] || '🌐';
  };

  const value = {
    language,
    setLanguage: changeLanguage,
    t,
    getLanguageName,
    getFlag,
    supportedLanguages,
    languageFlags,
    languageNames,
    isRTL: language === 'ar'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
